// components/KanbanBoard.js
// Board view for the Deals page — groups deals into columns by the
// company's real workflow stages (deal_stage_templates), so it stays in
// sync automatically as stages are added/renamed/reordered on /stages.
//
// Props:
//   deals     array  — deals already filtered/loaded by DealsListPage
//   stages    array  — workflow stages from GET /api/stages (id, name, rag_colour, position, product_type)
//   perms     object — usePermissions() result, for the same visibility rules the list view uses
//   navigate  fn     — react-router navigate, used to open a deal on card click

import React from 'react';

const COMMISSION_STAGE_META = {
  pipeline:  { bg: '#fef3c7', color: '#92400e' },
  payable:   { bg: '#dbeafe', color: '#1e40af' },
  paid:      { bg: '#d1fae5', color: '#065f46' },
  cancelled: { bg: '#fee2e2', color: '#991b1b' },
};

const RAG_BORDER = { red: '#ef4444', amber: '#f59e0b', green: '#10b981' };

function daysUntil(dateStr) {
  if (!dateStr) return null;
  return Math.ceil((new Date(dateStr) - new Date()) / 86400000);
}

function DealCard({ deal, perms, navigate }) {
  const gross = (parseFloat(deal.lender_proc_fee) || 0) + (parseFloat(deal.client_fee) || 0);
  const net = gross - (parseFloat(deal.network_fee_amount) || 0);
  const isArchived = deal.current_status_code === 'archived';
  const notProceeding = deal.current_status_code === 'not_proceeding';
  const dipDays = daysUntil(deal.dip_expires_at);
  const sm = COMMISSION_STAGE_META[deal.stage] || { bg: '#f3f4f6', color: '#6b7280' };

  return (
    <div
      onClick={() => navigate(`/deal/${deal.id}`)}
      style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10,
        padding: '12px 14px', marginBottom: 10, cursor: 'pointer',
        opacity: isArchived ? 0.6 : 1, transition: 'border-color 0.15s, box-shadow 0.15s' }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = '#10b981'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(16,185,129,0.1)'; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.boxShadow = 'none'; }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 6 }}>
        <span style={{ fontWeight: 700, color: '#0e1b2c', fontSize: 14, lineHeight: 1.3 }}>{deal.client_name}</span>
        {deal.reference && (
          <span style={{ fontSize: 10, fontWeight: 700, color: '#10b981', background: '#d1fae5', padding: '2px 7px', borderRadius: 9999, whiteSpace: 'nowrap' }}>
            {deal.reference}
          </span>
        )}
      </div>

      <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>
        {deal.introducer_name ? `🤝 via ${deal.introducer_name}` : '📋 Direct'}
        {perms.canViewTeamDeals && deal.created_by_name && <span> · {deal.created_by_name}</span>}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20,
          background: sm.bg, color: sm.color, textTransform: 'uppercase', letterSpacing: '0.03em' }}>
          {deal.stage}
        </span>
        {gross > 0 ? (
          <span style={{ fontWeight: 700, color: '#10b981', fontSize: 14 }}>
            £{net.toLocaleString('en-GB', { maximumFractionDigits: 0 })}
          </span>
        ) : (
          <span style={{ color: '#d1d5db', fontSize: 13 }}>—</span>
        )}
      </div>

      {(isArchived || notProceeding || dipDays !== null) && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {notProceeding && (
            <span style={{ fontSize: 10, fontWeight: 600, color: '#92400e', background: '#fef3c7', padding: '2px 7px', borderRadius: 9999 }}>
              Not Proceeding
            </span>
          )}
          {isArchived && (
            <span style={{ fontSize: 10, fontWeight: 600, color: '#6b7280', background: '#f3f4f6', padding: '2px 7px', borderRadius: 9999 }}>
              Archived
            </span>
          )}
          {dipDays !== null && (
            <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 9999,
              color: dipDays < 0 ? '#991b1b' : dipDays <= 7 ? '#92400e' : '#6b7280',
              background: dipDays < 0 ? '#fee2e2' : dipDays <= 7 ? '#fef3c7' : '#f3f4f6' }}>
              {dipDays < 0 ? `DIP expired ${Math.abs(dipDays)}d ago` : `DIP expires in ${dipDays}d`}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

export default function KanbanBoard({ deals, stages, perms, navigate }) {
  const grouped = {};
  deals.forEach(d => {
    const key = d.deal_stage_id || '__none__';
    (grouped[key] = grouped[key] || []).push(d);
  });

  const columns = stages.map(s => ({
    id: s.id,
    name: s.name,
    colour: s.rag_colour,
    productType: s.product_type,
    deals: grouped[s.id] || [],
  }));

  if (grouped.__none__) {
    columns.push({ id: '__none__', name: 'No stage', colour: null, deals: grouped.__none__ });
  }

  if (columns.length === 0) {
    return (
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '56px 24px', textAlign: 'center', color: '#6b7280' }}>
        No workflow stages configured yet — set them up under Stages.
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', gap: 14, overflowX: 'auto', paddingBottom: 8 }}>
      {columns.map(col => {
        const total = col.deals.reduce((s, d) =>
          s + (parseFloat(d.lender_proc_fee) || 0) + (parseFloat(d.client_fee) || 0) - (parseFloat(d.network_fee_amount) || 0), 0);
        const border = RAG_BORDER[col.colour] || '#d1d5db';

        return (
          <div key={col.id} style={{ minWidth: 260, width: 260, flexShrink: 0 }}>
            <div style={{ borderTop: `3px solid ${border}`, background: '#fff', border: '1px solid #e5e7eb',
              borderRadius: '10px 10px 0 0', padding: '10px 14px', marginBottom: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontWeight: 700, color: '#0e1b2c', fontSize: 13 }}>{col.name}</span>
                <span style={{ fontSize: 12, color: '#6b7280' }}>{col.deals.length}</span>
              </div>
              <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>
                {total > 0 ? `£${total.toLocaleString('en-GB', { maximumFractionDigits: 0 })} in stage` : 'No fees yet'}
              </div>
            </div>

            {col.deals.length === 0 ? (
              <div style={{ fontSize: 12, color: '#9ca3af', padding: '8px 4px' }}>No deals</div>
            ) : (
              col.deals.map(deal => <DealCard key={deal.id} deal={deal} perms={perms} navigate={navigate} />)
            )}
          </div>
        );
      })}
    </div>
  );
}
